import { cn } from '@/lib/utils'
import type { RedFlag } from '@/types'
import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'

/**
 * Pill that summarises detected red flags. Click to expand the full list
 * with severity + note. Renders nothing if there are no flags.
 */

interface RedFlagsPillProps {
  redFlags?: RedFlag[] | null | undefined
  className?: string
}

const SEVERITY_CLASSES: Record<RedFlag['severity'], string> = {
  low: 'bg-yellow-100 text-yellow-800',
  medium: 'bg-orange-100 text-orange-800',
  high: 'bg-red-100 text-red-800',
}

function formatType(t: string): string {
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function RedFlagsPill({ redFlags, className }: RedFlagsPillProps) {
  const [open, setOpen] = useState(false)
  const flags = redFlags ?? []
  if (flags.length === 0) return null

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-red-200 hover:bg-red-100 transition-colors"
      >
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        {flags.length} red flag{flags.length === 1 ? '' : 's'}
        <span className="text-red-500">{open ? '▴' : '▾'}</span>
      </button>
      {open ? (
        <ul className="flex flex-col gap-1 rounded-md border border-red-100 bg-red-50/50 p-2">
          {flags.map((flag, i) => (
            <li key={`${flag.type}-${i}`} className="flex flex-col gap-0.5 text-xs">
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                    SEVERITY_CLASSES[flag.severity],
                  )}
                >
                  {flag.severity}
                </span>
                <span className="font-medium text-gray-900">{formatType(flag.type)}</span>
              </div>
              <p className="text-gray-700 leading-snug pl-1">{flag.note}</p>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
